// Minimal JWT payload reader. No verification — the token is minted by the
// host and only ever presented back to the proxy; we read it to learn the
// user id and the expiry, nothing more.

/**
 * Decode a base64url segment to a UTF-8 string.
 * @param {string} input
 * @returns {string}
 */
export function base64UrlDecode(input) {
  // Replace URL-safe characters and add padding if needed
  let normalized = input.replace(/-/g, '+').replace(/_/g, '/');
  const pad = normalized.length % 4;
  if (pad) {
    normalized += '='.repeat(4 - pad);
  }

  // Decode the base64 string
  return atob(normalized);
}

/**
 * Decode a JWT's payload. Returns null for anything unparseable rather than
 * throwing — a malformed token must not break boot.
 *
 * @param {string} token
 * @returns {Object|null}
 */
export function decodeJwtPayload(token) {
  if (!token || typeof token !== 'string') return null;
  const parts = token.split('.');
  if (parts.length < 2 || !parts[1]) return null;
  try {
    return JSON.parse(base64UrlDecode(parts[1]));
  } catch (error) {
    console.warn('Could not decode JWT payload:', error.message);
    return null;
  }
}

/**
 * Expiry of a JWT in milliseconds since the epoch, or null when the token has
 * no usable `exp` claim.
 *
 * @param {string} token
 * @returns {number|null}
 */
export function getJwtExpiryMs(token) {
  const payload = decodeJwtPayload(token);
  const exp = payload && payload.exp;
  return typeof exp === 'number' && Number.isFinite(exp) ? exp * 1000 : null;
}

/**
 * First 8 characters of a token, for log lines. Never log a whole token.
 * @param {string} token
 * @returns {string}
 */
export const maskToken = (token) =>
  typeof token === 'string' && token ? `${token.slice(0, 8)}…` : '(none)';
